import React, { useState } from 'react';
import Seo from '../components/Seo';
import { Flame, ArrowRight, Check, Heart, Ear, Target, Phone } from 'lucide-react';
import { FIRESIDE_PRICING, FIRESIDE_FLOW, BRAND } from '../mock/mock';
import Reveal from '../components/Reveal';
import { useToast } from '../hooks/use-toast';
import { submitFireside } from '../lib/apiClient';

const NEED_LIST = [
  'You keep going back and forth about a major decision.',
  'You’re navigating divorce, separation, dating or relationship confusion.',
  'You’re dealing with co-parenting drama and need perspective.',
  'Your children are grown and you’re trying to rediscover yourself.',
  'You’ve been forced into a season of singleness you didn’t choose.',
  'You’re carrying something you can’t discuss with family or friends.',
  'Everybody has an opinion, but nobody seems to be listening to you.',
  'You know something has to change — you just don’t know your next move.',
];

const Fireside = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', plan: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Almost there', description: 'Please add your name and email so we can reach you.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitFireside(form);
      toast({ title: 'Pull up a chair', description: 'Your Fireside Clarity Call request is in. We’ll be in touch soon.' });
      setForm({ name: '', email: '', plan: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-fire-ink">
      <Seo title="Fireside Clarity Calls" path="/fireside" description="Private one-on-one Fireside Clarity Calls with Dr. Gee — a safe, faith-based space to hear yourself think and find your next step after the fire." />
      {/* Hero banner */}
      <section className="pt-24">
        <div className="relative">
          <img
            src="/brand/fireside-banner.jpg"
            alt="Fireside Clarity"
            className="w-full h-[460px] md:h-[500px] object-cover object-[62%_32%] md:object-right"
          />
          {/* Mobile: darken from the bottom so Dr. Gee stays visible up top */}
          <div className="absolute inset-0 md:hidden bg-gradient-to-t from-fire-ink via-fire-ink/55 to-transparent" />
          {/* Desktop: original left-to-right fade */}
          <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-fire-ink via-fire-ink/70 to-transparent" />
          <div className="absolute inset-0 flex items-end md:items-center pb-10 md:pb-0">
            <div className="max-w-[1400px] mx-auto px-5 lg:px-8 w-full">
              <div className="max-w-xl">
                <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">AFTER THE FIRE • REAL TALK. REAL CLARITY.</span>
                <h1 className="font-script text-6xl md:text-7xl text-fire-goldlight mt-3 leading-none">Fireside</h1>
                <h2 className="font-marcellus tracking-[0.3em] text-fire-cream text-2xl md:text-3xl">CLARITY</h2>
                <p className="text-fire-cream/75 font-body mt-5 leading-relaxed">
                  Sometimes you don’t need another opinion. You need a safe place to tell the whole truth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote strip */}
      <section className="bg-gradient-to-r from-fire-maroondark via-fire-maroon to-fire-maroondark py-8">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <p className="font-cormorant text-xl md:text-2xl text-fire-cream italic">
            “You tell me what happened. We’ll talk about what it means. Then we’ll figure out what comes next.” <span className="text-fire-goldlight not-italic font-body text-sm">— Dr. Gee</span>
          </p>
        </div>
      </section>

      {/* What is it + 3 pillars */}
      <section className="py-24 ember-texture">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <Reveal>
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">WHAT IS A FIRESIDE CLARITY CALL?</span>
            <h2 className="font-display text-3xl md:text-4xl text-fire-cream mt-3 mb-6">Real Talk. Real Clarity. Real Next Steps.</h2>
            <p className="text-fire-cream/70 font-body leading-relaxed max-w-2xl mx-auto">
              A private, one-on-one conversation with Dr. Gee designed to give you room to talk honestly about what is happening in your life. This is not about somebody telling you what to do. It’s about having an experienced, compassionate and unbiased ear.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5 mt-12">
            {[
              { icon: Heart, t: 'A Safe Space', d: 'Confidential. Compassionate. Judgment-free.' },
              { icon: Ear, t: 'An Unbiased Ear', d: 'I can listen without needing to pick a side.' },
              { icon: Target, t: 'Clarity & a Plan', d: 'Walk away with insight, encouragement and next steps.' },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="card-dark rounded-2xl p-8 h-full hover-lift">
                  <c.icon className="w-8 h-8 text-fire-goldlight mx-auto mb-4" strokeWidth={1.4} />
                  <h3 className="font-display text-xl text-fire-cream mb-2">{c.t}</h3>
                  <p className="text-fire-cream/60 font-body text-sm">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* You may need a call if */}
      <section className="py-24 bg-fire-inksoft">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-12">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">YOU MAY NEED A FIRESIDE CALL IF…</span>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4">
            {NEED_LIST.map((n, i) => (
              <Reveal key={i} delay={(i % 2) * 60}>
                <div className="flex items-start gap-3 card-dark rounded-xl px-5 py-4">
                  <Flame className="w-5 h-5 text-fire-gold shrink-0 mt-0.5" strokeWidth={1.6} />
                  <p className="text-fire-cream/75 font-body text-sm">{n}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The call flow */}
      <section className="py-24 ember-texture">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">WHAT HAPPENS DURING OUR CALL</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">Your 60 Minutes</h2>
            <p className="text-fire-cream/60 font-body mt-4">Bring your coffee, tea, journal and your truth. You talk. I listen. Then together, we sort through the smoke.</p>
          </Reveal>
          <div className="relative pl-6 md:pl-8">
            <div className="absolute left-[10px] md:left-3 top-2 bottom-2 w-px bg-fire-gold/30" />
            {FIRESIDE_FLOW.map((s, i) => (
              <Reveal key={i} delay={i * 60} className="relative mb-8 last:mb-0">
                <span className="absolute -left-[27px] md:-left-[33px] top-1 w-3.5 h-3.5 rounded-full bg-fire-gold ring-4 ring-fire-ink" />
                <p className="font-marcellus tracking-widest text-fire-gold text-[11px] mb-1">{s.t}</p>
                <h3 className="font-display text-xl text-fire-cream">{s.h}</h3>
                <p className="text-fire-cream/60 font-body text-sm mt-1">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-fire-inksoft">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">BOOK YOUR CALL</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">Pull Up a Chair</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5 items-stretch">
            {FIRESIDE_PRICING.map((p, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className={`rounded-2xl p-8 h-full flex flex-col hover-lift ${p.featured ? 'bg-gradient-to-b from-fire-maroon to-fire-maroondark border border-fire-gold/40' : 'card-dark'}`}>
                  {p.featured && <span className="self-start bg-fire-gold text-fire-ink text-[10px] font-bold tracking-widest px-3 py-1 rounded-full mb-3">BEST VALUE</span>}
                  <h3 className="font-display text-2xl text-fire-cream">{p.name}</h3>
                  <div className="my-3"><span className="font-display text-4xl text-gradient-gold">{p.price}</span></div>
                  <p className="text-fire-cream/60 font-body text-sm mb-6">{p.desc}</p>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-fire-cream/75 font-body text-sm">
                        <Check className="w-4 h-4 text-fire-gold shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#fireside-form" onClick={() => update('plan', p.name)} className={`rounded-full py-3 text-center font-semibold text-sm ${p.featured ? 'btn-gold' : 'btn-outline-gold'}`}>Book This</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="fireside-form" className="py-24 ember-texture">
        <div className="max-w-[720px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-9">
            <Phone className="w-8 h-8 text-fire-gold mx-auto mb-4" strokeWidth={1.4} />
            <h2 className="font-display text-3xl md:text-4xl text-fire-cream">Book My Fireside Call</h2>
            <p className="text-fire-cream/60 font-body mt-3">60-minute private conversation with Dr. Gee.</p>
          </Reveal>
          <Reveal delay={100}>
            <form onSubmit={submit} className="card-dark rounded-2xl p-7 md:p-9 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <FField label="Your Name *" value={form.name} onChange={(v) => update('name', v)} placeholder="Jane Doe" />
                <FField label="Email *" type="email" value={form.email} onChange={(v) => update('email', v)} placeholder="you@example.com" />
              </div>
              <div>
                <label className="block font-body text-sm text-fire-cream/70 mb-2">Which option?</label>
                <select value={form.plan} onChange={(e) => update('plan', e.target.value)} className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream focus:outline-none focus:border-fire-gold transition-colors">
                  <option value="">Select an option</option>
                  {FIRESIDE_PRICING.map((p) => <option key={p.name} value={p.name}>{p.name} — {p.price}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-body text-sm text-fire-cream/70 mb-2">Anything you’d like Dr. Gee to know? (optional)</label>
                <textarea value={form.message} onChange={(e) => update('message', e.target.value)} rows={3} placeholder="You don’t have to have the right words." className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors resize-none" />
              </div>
              <button type="submit" disabled={submitting} className="btn-gold w-full rounded-full py-4 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
                {submitting ? 'Sending…' : (<>Book My Fireside Call <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
            <p className="text-center text-fire-cream/40 font-body text-xs mt-6 max-w-lg mx-auto">
              Fireside Clarity Calls provide peer support, perspective and personal-development guidance based on lived experience. They are not psychotherapy, mental-health treatment, legal advice, medical advice or crisis services.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const FField = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="block font-body text-sm text-fire-cream/70 mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors" />
  </div>
);

export default Fireside;
